import { z } from "zod";
import { groupCategoryEmbeddedSchema, groupRoleSchema } from "./groups.js";

/**
 * Shared DTO cho Invitations — POST /v1/groups/:id/invitations/email
 * (US-E4.F4-0023 / @UC0027 — Invite Member via Email).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN Phase 3: invitations thuộc Groups Phase 3 (module invitations,
 * `POST /v1/groups/:id/invitations`). Slice 0023 = kênh EMAIL: tạo Invitation row pending
 * (TTL 7 ngày), channel='link', matchKeyType='email'. Owner/Admin only (GroupRoleGuard).
 *
 * ⚠️ no-SMS (C1): kênh email/link DUY NHẤT. Invitation.channel enum KHÔNG có 'sms' —
 * KHÔNG có code path SMS ở bất kỳ tầng nào.
 *
 * 📨 Delivery email = DEFER Phase 8.5 (worker dispatcher). Endpoint chỉ tạo row + enqueue
 * outbox event (transactional outbox như forgot-reset); gửi email thật do worker xử lý sau.
 */

/** Số email tối đa trong 1 request mời (chống over-post / DoS). */
export const INVITE_EMAILS_MAX = 50;

/** Số email tối thiểu trong 1 request (ít nhất 1 — AC1). */
export const INVITE_EMAILS_MIN = 1;

/** Độ dài email tối đa tại biên (RFC giới hạn ~254; chặn input phình to). */
export const INVITE_EMAIL_MAX_LENGTH = 254;

/** TTL của lời mời email = 7 ngày (invariant story — link hết hạn sau 7 ngày). */
export const INVITE_TTL_DAYS = 7;

/** Channel của Invitation tạo qua kênh email — 'link' (mở link join từ email). KHÔNG 'sms'. */
export const INVITE_CHANNEL_LINK = "link" as const;

/** Loại match key cho lời mời email. KHÔNG dùng 'phone' (no-SMS). */
export const INVITE_MATCH_KEY_EMAIL = "email" as const;

/**
 * Trạng thái lời mời (khớp schema.prisma Invitation.status). Một nguồn sự thật cho api + test.
 * Slice 0023 tạo 'pending'; accept/decline (story 0024) chuyển trạng thái sau.
 */
export const INVITATION_STATUS = {
  PENDING: "pending",
  MATCHED: "matched",
  ACCEPTED: "accepted",
  DECLINED: "declined",
  EXPIRED: "expired",
  REVOKED: "revoked",
} as const;

export const invitationStatusSchema = z.enum([
  INVITATION_STATUS.PENDING,
  INVITATION_STATUS.MATCHED,
  INVITATION_STATUS.ACCEPTED,
  INVITATION_STATUS.DECLINED,
  INVITATION_STATUS.EXPIRED,
  INVITATION_STATUS.REVOKED,
]);
export type InvitationStatus = z.infer<typeof invitationStatusSchema>;

/** Thông điệp lỗi (một nguồn sự thật api ↔ mobile) — AC3/AC4. */
export const INVITE_ALREADY_MEMBER_MESSAGE = "Người dùng này đã là thành viên.";
export const INVITE_INVALID_EMAIL_MESSAGE = "Email không hợp lệ.";
export const INVITE_ALREADY_PENDING_MESSAGE =
  "Đã có lời mời đang chờ cho email này.";

/**
 * Một email đơn lẻ trong body: trim + lowercase + email format. citext ở DB so không phân biệt
 * hoa-thường; client/biên vẫn chuẩn hoá để dedup nhất quán + lưu canonical.
 */
export const inviteEmailItemSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, INVITE_INVALID_EMAIL_MESSAGE)
  .max(INVITE_EMAIL_MAX_LENGTH, INVITE_INVALID_EMAIL_MESSAGE)
  .email(INVITE_INVALID_EMAIL_MESSAGE);

/**
 * Body cho POST /v1/groups/:id/invitations/email.
 * - emails BẮT BUỘC: mảng 1..50 email; mỗi email trim+lowercase+format (AC1/AC2/AC4).
 *   Mảng rỗng → reject; >50 → reject (chống over-post/DoS).
 * - `.strict()` chặn field lạ tại biên (chống over-post: KHÔNG nhận groupId/role/token/status
 *   từ body — server quyết toàn bộ).
 */
export const inviteEmailRequestSchema = z
  .object({
    emails: z
      .array(inviteEmailItemSchema)
      .min(INVITE_EMAILS_MIN, "Cần ít nhất 1 email.")
      .max(INVITE_EMAILS_MAX, `Tối đa ${INVITE_EMAILS_MAX} email mỗi lần.`),
  })
  .strict();

export type InviteEmailRequest = z.infer<typeof inviteEmailRequestSchema>;

/**
 * Kết quả per-email (AC2 — multi-email trả kết quả cho từng email):
 *  - email: email đã chuẩn hoá (canonical) — KHÔNG là raw input.
 *  - status: 'created' (Invitation pending mới tạo) | 'already_member' (AC3) |
 *    'already_pending' (invariant dedup: đã có pending invite cho email này).
 *  - invitationId: id Invitation khi status='created'; null cho các trường hợp lỗi.
 * `.strict()` chặn rò field thừa (token/matchKeyValue raw) ra response (C2 — KHÔNG lộ token).
 */
export const INVITE_RESULT_STATUS = {
  CREATED: "created",
  ALREADY_MEMBER: "already_member",
  ALREADY_PENDING: "already_pending",
} as const;

export const inviteResultStatusSchema = z.enum([
  INVITE_RESULT_STATUS.CREATED,
  INVITE_RESULT_STATUS.ALREADY_MEMBER,
  INVITE_RESULT_STATUS.ALREADY_PENDING,
]);
export type InviteResultStatus = z.infer<typeof inviteResultStatusSchema>;

export const inviteEmailResultItemSchema = z
  .object({
    email: z.string(),
    status: inviteResultStatusSchema,
    invitationId: z.string().uuid().nullable(),
  })
  .strict();

export type InviteEmailResultItem = z.infer<typeof inviteEmailResultItemSchema>;

/**
 * Shape response cho POST /v1/groups/:id/invitations/email.
 *  - results: kết quả per-email (created / already_member / already_pending).
 *  - createdCount: số Invitation pending mới tạo (≥0) — UX toast "Đã gửi lời mời đến [email]".
 * `.strict()` chặn rò field thừa (token/secret) ra response (C2).
 *
 * ⚠️ KHÔNG trả token/matchKeyValue raw — chống lộ PII/token (C2 redaction).
 */
export const inviteEmailResponseSchema = z
  .object({
    results: z.array(inviteEmailResultItemSchema),
    createdCount: z.number().int().min(0),
  })
  .strict();

export type InviteEmailResponse = z.infer<typeof inviteEmailResponseSchema>;

/* ===========================================================================
 * Open Invite-Link — owner/admin phát hành link mời MỞ (auto-join, DR-02) — US-E13.F2-0108-S09B
 * (@UC0027). Khác kênh EMAIL (bind email người nhận): link MỞ KHÔNG bind email — bất kỳ ai đã đăng
 * nhập cầm link hợp lệ (còn hạn) đều AUTO-JOIN ngay (không cần owner duyệt). Invitation row:
 * channel='link', matchKeyValue=null (⇒ open). Owner/Admin only (GroupRoleGuard).
 *
 * ⚠️ Token thô TRẢ 1 LẦN (để client dựng shareable-link) — server CHỈ lưu HASH (C2). Response sau
 * đó KHÔNG bao giờ trả lại token. AI cầm token cũng join được ⇒ BẮT BUỘC expiry (7 ngày) + owner
 * revoke + rate-limit create/accept (guardrails). KHÔNG SMS (C1).
 * ========================================================================= */

/**
 * Body cho POST /v1/groups/:id/invitations/link — tạo link mời MỞ. KHÔNG cần field nào (server
 * quyết toàn bộ: channel/token/expiry/status). `.strict()` chặn over-post (KHÔNG nhận
 * groupId/token/status/email từ body — chống tự đặt token/expiry).
 */
export const createInviteLinkRequestSchema = z.object({}).strict();

export type CreateInviteLinkRequest = z.infer<
  typeof createInviteLinkRequestSchema
>;

/**
 * Shape response cho POST create-link (201). Token thô TRẢ 1 LẦN (client dựng shareable-link
 * `<base>/join?token=<token>`); server chỉ lưu hash ⇒ KHÔNG lấy lại được sau lần này.
 *  - invitationId: id Invitation row (owner có thể revoke sau).
 *  - token: token THÔ (1 lần) — client KHÔNG gửi lại server, dùng dựng link.
 *  - expiresAt: ISO — link hết hạn sau 7 ngày (UI hiển thị "hết hạn ...").
 * `.strict()` chặn rò field thừa (matchKeyValue/secret) ra response (C2).
 */
export const createInviteLinkResponseSchema = z
  .object({
    invitationId: z.string().uuid(),
    token: z.string().min(1),
    expiresAt: z.string(),
  })
  .strict();

export type CreateInviteLinkResponse = z.infer<
  typeof createInviteLinkResponseSchema
>;

/**
 * Shape response cho DELETE revoke-link (200) — owner/admin thu hồi link MỞ (status→revoked, void
 * token). Idempotent (đã revoke/không pending → vẫn 200 status revoked). `.strict()`.
 */
export const revokeInviteLinkResponseSchema = z
  .object({
    invitationId: z.string().uuid(),
    status: z.literal(INVITATION_STATUS.REVOKED),
  })
  .strict();

export type RevokeInviteLinkResponse = z.infer<
  typeof revokeInviteLinkResponseSchema
>;

/* ===========================================================================
 * US-E24.F10-0359-S03 (@UC9628 @UC9629) — GET /v1/groups/:id/invitations/link.
 *
 * VÌ SAO CÓ: trước task này surface link mở có `post` + `delete/:invId` và **0 method `get`**. Vào
 * màn D6 không thấy link đang có ⇒ bấm "Tạo link" ⇒ link THỨ HAI ra đời, link thứ nhất VẪN SỐNG và
 * vẫn auto-join được, người tạo KHÔNG còn `invitationId` để revoke. Lặp vài lần ⇒ N vé vào nhóm
 * song song, không quản được cái nào. Nghiêm trọng hơn link minh bạch một bậc: link mời cho người
 * lạ vào THẲNG nhóm.
 *
 * 🔴 PHƯƠNG ÁN 1 ĐÃ CHỐT (index mục 7 câu 2): **KHÔNG trả `token` thô.** Không phải lựa chọn khẩu
 * vị — DB CHỈ lưu hash (`token` cột này là hash, xem `invite-link.service.ts` C2), token thô không
 * tồn tại ở đâu để trả. Trả lại được ⇒ phải lưu thêm plaintext/mã hoá đối xứng + migration + quản
 * lý khoá ⇒ ĐẢO NGƯỢC bất biến ngay phía trên ("KHÔNG lấy lại được sau lần này"). Đó là đổi MÔ HÌNH
 * ĐE DOẠ, phải là task riêng có @gate bảo mật.
 * ⚠️ Đánh đổi thật, không giấu: chủ nhóm KHÔNG copy lại được link cũ — muốn chia sẻ lại thì revoke
 * rồi tạo mới. Đổi lại: token không bao giờ tồn tại ngoài lần trả đầu tiên.
 * ========================================================================= */

/** Trần limit + độ dài cursor (mirror các list khác của repo). */
export const INVITE_LINKS_MAX_LIMIT = 50;
export const INVITE_LINKS_MAX_CURSOR_LENGTH = 512;

/**
 * Hình dạng token link mời: base64url dài (`issue()` sinh 32 byte ⇒ ~43 ký tự). Dùng làm LƯỚI QUÉT
 * trong test — bắt cả trường hợp token rò ra dưới một TÊN FIELD KHÁC (đổi tên không phải là che).
 *
 * ⚠️ Quét trên `data`, KHÔNG quét envelope: `pageInfo.nextCursor` là cursor opaque base64url và sẽ
 * khớp hình dạng này — guard hay báo động giả là guard sẽ bị tắt.
 */
export const INVITE_LINK_TOKEN_SHAPE = /[A-Za-z0-9_-]{40,}/;

/**
 * 1 dòng link mời ĐANG HIỆU LỰC. `invitationId` đặt trùng tên param của
 * `DELETE .../invitations/link/:invId` về mặt ngữ nghĩa (id đó ghép thẳng vào URL revoke).
 * `status` khoá cứng `pending`: danh sách này là "đang hiệu lực", KHÔNG phải sổ lưu trữ — trả kèm
 * revoked/accepted sẽ khiến người dùng tưởng mình còn phải đi tắt những cái đã tắt.
 * `.strict()` — KHÔNG `token`, KHÔNG hash.
 */
export const inviteLinkListItemSchema = z
  .object({
    invitationId: z.string().uuid(),
    status: z.literal(INVITATION_STATUS.PENDING),
    /** null = link KHÔNG hạn — ca xấu PHẢI hiện ra để còn thu hồi, không phải ca để giấu. */
    expiresAt: z.string().nullable(),
    createdAt: z.string(),
    invitedBy: z.string().uuid().nullable(),
  })
  .strict();

export type InviteLinkListItem = z.infer<typeof inviteLinkListItemSchema>;

/** Envelope cursor chuẩn repo. Nhóm chưa từng tạo link ⇒ `data: []` (KHÔNG 404). */
export const inviteLinksListResponseSchema = z
  .object({
    data: z.array(inviteLinkListItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type InviteLinksListResponse = z.infer<typeof inviteLinksListResponseSchema>;

/** Query phân trang — tập có thể > 1 (di sản từ chính bug "đẻ link mới mỗi lần mở màn"). */
export const inviteLinksListQuerySchema = z.object({
  cursor: z.string().max(INVITE_LINKS_MAX_CURSOR_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(INVITE_LINKS_MAX_LIMIT).optional(),
});

export type InviteLinksListQuery = z.infer<typeof inviteLinksListQuerySchema>;

/* ===========================================================================
 * Accept / Decline / Preview Group Invite — phía NGƯỜI NHẬN (US-E4.F4-0024 / @UC0012, @UC0013).
 *
 * POST   /v1/groups/:id/invitations/accept    (token trong BODY)
 * POST   /v1/groups/:id/invitations/decline   (token trong BODY)
 * POST   /v1/groups/:id/invitations/preview   (token trong BODY — xem-trước trước accept)
 *
 * ⚠️ TOKEN ĐI QUA BODY, KHÔNG path segment (H1 review PR#44 — C2/C3): token thô KHÔNG được lọt vào
 * access-log `req.url` / ProblemDetail `instance` (=request.path). pino redact sẵn `req.body.token`.
 *
 * Token-bound: token thô (body) băm sha256 đối chiếu Invitation.token (HASH lưu DB).
 * Caller (UserAuthGuard) phải là người nhận: email canonical của user khớp matchKeyValue (chống IDOR).
 * Token void sau accept/decline (status đổi sang accepted/declined → KHÔNG còn pending → không tái dùng).
 *
 * BẢO MẬT (C2/C4): response KHÔNG mang token raw / email người khác / secret. `.strict()`.
 * ========================================================================= */

/**
 * Độ dài tối đa token thô tại biên (chặn over-post/DoS). Token issue() = 32 byte base64url (~43 ký
 * tự); dư tới 256 cho dung sai. Token rỗng/quá dài → reject 400 (KHÔNG chạm DB).
 */
/**
 * 🔴 US-E24.F20-0375 (@UC9653 @UC9654) — mã lỗi của link mời sau khi siết.
 *
 * `INVITE_LINK_EXHAUSTED`: link đã dùng hết số lượt cho phép (`maxUses`).
 * `INVITE_OPEN_LINK_CANNOT_CLAIM_EXISTING`: link MỞ không được nhận một suất **đã có người đại
 * diện** — người lạ cầm link chuyển tiếp không được trở thành đại diện tiền của con nhà khác.
 */
export const INVITE_LINK_CODE = {
  EXHAUSTED: "INVITE_LINK_EXHAUSTED",
  OPEN_LINK_CANNOT_CLAIM_EXISTING: "INVITE_OPEN_LINK_CANNOT_CLAIM_EXISTING",
} as const;
export const INVITE_LINK_EXHAUSTED_MESSAGE =
  "Link mời này đã dùng hết số lượt cho phép. Hãy xin người quản lý nhóm gửi link mới.";
export const INVITE_OPEN_LINK_CLAIM_MESSAGE =
  "Suất này đã có người đại diện. Hãy xin người quản lý nhóm mời bạn trực tiếp qua email.";

export const INVITE_TOKEN_MAX_LENGTH = 256;

/**
 * Body cho accept/decline/preview invite — token đi qua BODY (KHÔNG path segment) để token thô
 * KHÔNG lọt vào access-log `req.url` / ProblemDetail `instance` (H1 review PR#44, C2/C3). pino đã
 * redact `req.body.token` sẵn ⇒ token bị che `[REDACTED]` trong log.
 *
 * - token BẮT BUỘC: 1..256 ký tự (server băm sha256 đối chiếu Invitation.token HASH).
 * - `.strict()` chặn field lạ tại biên (over-post: KHÔNG nhận groupId/status/role từ body — server
 *   quyết; groupId lấy từ path `:id`).
 */
export const inviteTokenRequestSchema = z
  .object({
    token: z
      .string()
      .trim()
      .min(1, "Thiếu token lời mời.")
      .max(INVITE_TOKEN_MAX_LENGTH, "Token lời mời không hợp lệ."),
  })
  .strict();

export type InviteTokenRequest = z.infer<typeof inviteTokenRequestSchema>;

/** Thông điệp lỗi (một nguồn sự thật api ↔ mobile) — AC3/AC4/void. */
export const INVITE_EXPIRED_MESSAGE =
  "Lời mời này đã hết hạn. Liên hệ Admin để được mời lại.";
export const INVITE_ALREADY_HANDLED_MESSAGE =
  "Lời mời này đã được xử lý.";
export const INVITE_NOT_RECIPIENT_MESSAGE =
  "Lời mời này không dành cho bạn.";
export const INVITE_ALREADY_GROUP_MEMBER_MESSAGE =
  "Bạn đã là thành viên nhóm này.";

/**
 * Shape response cho POST accept — đủ thông tin để client điều hướng Group Profile (AC1):
 *  - groupId/groupName/avatarUrl: header nhóm để điều hướng + hiển thị.
 *  - role: vai trò của caller sau khi tham gia — luôn 'member' (R7 Phase 3).
 *  - needsIdentitySelection (US-IDM.F2-0289-S06 · @UC9515): chỉ true khi chế độ ánh xạ = 'manual'
 *    (nhóm parent). Khi đó accept KHÔNG tự tạo GroupMember/self-account; client phải dẫn user vào
 *    màn One-Time Identity Selection (S07). Mặc định false/vắng ⇒ auto (đã là member).
 * `.strict()` chặn rò token/email/secret ra response (C2).
 */
export const acceptInviteResponseSchema = z
  .object({
    groupId: z.string().uuid(),
    groupName: z.string(),
    avatarUrl: z.string().nullable(),
    role: groupRoleSchema,
    needsIdentitySelection: z.boolean().optional(),
  })
  .strict();

export type AcceptInviteResponse = z.infer<typeof acceptInviteResponseSchema>;

/**
 * Shape response cho POST decline (AC2). Chỉ xác nhận đã từ chối + groupId (client điều hướng Home).
 * `.strict()` chặn rò field thừa ra response (C2).
 */
export const declineInviteResponseSchema = z
  .object({
    groupId: z.string().uuid(),
    status: z.literal(INVITATION_STATUS.DECLINED),
  })
  .strict();

export type DeclineInviteResponse = z.infer<typeof declineInviteResponseSchema>;

/**
 * Trạng thái màn preview lời mời (GET) — driver UI client (AC1/AC3/AC4):
 *  - 'pending': lời mời còn hiệu lực → hiển thị card + nút Tham gia/Từ chối.
 *  - 'expired': link >7 ngày → màn hết hạn (AC3).
 *  - 'already_member': caller đã là member active → "Bạn đã là thành viên" + "Vào nhóm" (AC4).
 */
export const INVITE_PREVIEW_STATE = {
  PENDING: "pending",
  EXPIRED: "expired",
  ALREADY_MEMBER: "already_member",
} as const;

export const invitePreviewStateSchema = z.enum([
  INVITE_PREVIEW_STATE.PENDING,
  INVITE_PREVIEW_STATE.EXPIRED,
  INVITE_PREVIEW_STATE.ALREADY_MEMBER,
]);
export type InvitePreviewState = z.infer<typeof invitePreviewStateSchema>;

/**
 * Shape response cho GET preview (màn xem-trước trước khi accept):
 *  - state: pending | expired | already_member — driver UI.
 *  - groupId/groupName/avatarUrl/memberCount: thông tin nhóm để hiển thị card preview.
 *  - invitedByName: tên người mời (Invitation.invitedBy → User.fullName); null khi không xác định.
 * KHÔNG trả token raw / email người nhận / secret (C2). `.strict()`.
 */
export const invitePreviewResponseSchema = z
  .object({
    state: invitePreviewStateSchema,
    groupId: z.string().uuid(),
    groupName: z.string(),
    avatarUrl: z.string().nullable(),
    memberCount: z.number().int().min(0),
    invitedByName: z.string().nullable(),
    /**
     * US-E4.F1-0431-S02 (@UC9677) — LOẠI NHÓM + gói từ vựng của nhóm đang mời.
     *
     * 🔴 Vì sao PHẢI có ở đây: luồng mời chiếm 14/24 nhãn ("Nhập tên học sinh", "Bạn là phụ huynh
     * của ai?"…) và xảy ra TRƯỚC khi vào nhóm — người được mời CHƯA là member nên không gọi được
     * `GET /v1/groups/:id` (guard membership trả 404). Bỏ sót chỗ này là hơn nửa số nhãn không
     * đổi được, đúng nhóm màn hình mà từ vựng sai gây bối rối nhất.
     *
     * C2: chỉ NHÃN + luật hiển thị — 0 field tiền, 0 PII, không lộ thêm gì về nhóm mà người được
     * mời chưa có quyền biết (họ đã thấy tên nhóm/ảnh/sĩ số ngay bên trên).
     * BẮT BUỘC có mặt (nullable): nhóm chưa gán loại ⇒ `null`.
     */
    category: groupCategoryEmbeddedSchema.nullable(),
  })
  .strict();

export type InvitePreviewResponse = z.infer<typeof invitePreviewResponseSchema>;

/* ===========================================================================
 * US-E24.F11-0360-S01 (@UC9602) — GET /v1/me/invitations.
 *
 * VÌ SAO CÓ: 12 path `/v1/me*` và không cái nào là `invitations`. Ba đường duy nhất chạm lời mời
 * (`preview`/`accept`/`decline`) đều đòi **token trong body**. Lời mời đi qua email/Zalo; người
 * dùng đọc trên máy khác rồi mới cài app ⇒ mất link là mất đường vào nhóm, dù server đã biết thừa
 * ai được mời (`matchedUserId` + `identity_index`).
 *
 * 🔴 "CHÍNH TÔI" RESOLVE TỪ TOKEN. Query `.strict()` KHÔNG có `email`/`userId`/`memberId` — một
 * tham số định danh trên route này biến nó thành máy dò lời mời của người khác. Bất biến nằm ở
 * BIÊN (zod), không ở service: field lạ chết trước khi có câu truy vấn nào chạy.
 *
 * 🔴 KHÔNG `token` (kể cả hash). Trả metadata thì người dùng biết *mình đang được mời vào đâu, còn
 * hạn tới bao giờ, ai mời* — đủ để đi tìm lại link hoặc nhắn người mời gửi lại. Accept bằng
 * `invitationId` là **endpoint GHI mới** + mô hình đe doạ khác (bỏ lớp "phải cầm token") ⇒ task
 * riêng có @gate, KHÔNG nhét vào một surface đọc-thuần.
 * ========================================================================= */

/** Trần limit + độ dài cursor (mirror các list khác của repo). */
export const MY_INVITATIONS_MAX_LIMIT = 50;
export const MY_INVITATIONS_MAX_CURSOR_LENGTH = 512;

/**
 * 1 dòng "lời mời gửi tới tôi". `invitedByName` = tên hiển thị (mirror `invitePreviewResponse`),
 * KHÔNG email/phone người mời. `expiresAt` null = link không hạn (ca có thật, phải hiện ra).
 * `.strict()` — thêm field mà quên sửa hợp đồng ⇒ ĐỎ ở biên chứ không rò lặng lẽ.
 */
export const myInvitationListItemSchema = z
  .object({
    invitationId: z.string().uuid(),
    groupId: z.string().uuid(),
    groupName: z.string(),
    channel: z.string(),
    status: invitationStatusSchema,
    expiresAt: z.string().nullable(),
    invitedByName: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export type MyInvitationListItem = z.infer<typeof myInvitationListItemSchema>;

/** Envelope cursor chuẩn repo. Chưa ai mời ⇒ `data: []` (KHÔNG 404 — "không có việc treo" là câu trả lời hợp lệ). */
export const myInvitationsListResponseSchema = z
  .object({
    data: z.array(myInvitationListItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type MyInvitationsListResponse = z.infer<typeof myInvitationsListResponseSchema>;

/**
 * 🔴 `.strict()` LÀ MỘT AC, không phải thói quen: `?email=`/`?userId=` phải trả 400 ở biên.
 * Các query list khác của repo không strict vì chúng đã bound tenant bằng guard; route `/v1/me/*`
 * thì bound bằng CHÍNH TOKEN, nên tham số định danh là thứ duy nhất có thể phá được nó.
 */
export const myInvitationsListQuerySchema = z
  .object({
    cursor: z.string().max(MY_INVITATIONS_MAX_CURSOR_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(MY_INVITATIONS_MAX_LIMIT).optional(),
    status: invitationStatusSchema.optional(),
  })
  .strict();

export type MyInvitationsListQuery = z.infer<typeof myInvitationsListQuerySchema>;
